import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Upload, Loader2, CheckCircle2, AlertTriangle, ExternalLink,
  ShoppingBag, ReceiptText, ClipboardList, Wallet, Banknote, FileText,
  Info, RefreshCw,
} from 'lucide-react';

// Provider brand chip
const ProviderChip = ({ provider }) => {
  if (!provider) return null;
  return (
    <Badge
      variant="outline"
      className="font-medium"
      style={{ borderColor: provider.colour, color: provider.colour }}
      data-testid={`provider-chip-${provider.id}`}
    >
      {provider.name}
    </Badge>
  );
};

const TAB_ORDER = [
  { id: 'sales', label: 'Sales & Revenue', icon: ShoppingBag, kind: 'pos' },
  { id: 'orders', label: 'Orders', icon: ClipboardList, kind: 'pos' },
  { id: 'product_mix', label: 'Product Mix', icon: ReceiptText, kind: 'pos' },
  { id: 'payroll', label: 'Payroll & Time', icon: Wallet, kind: 'common' },
  { id: 'bank', label: 'Bank Statements', icon: Banknote, kind: 'common' },
  { id: 'receipts', label: 'Receipts & Invoices', icon: FileText, kind: 'common' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium', timeStyle: 'short',
    });
  } catch {
    return iso;
  }
};

export default function ImportsPage() {
  const { api, currentLocation } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [catalog, setCatalog] = useState(null);              // { providers, common_tabs, supported }
  const [effective, setEffective] = useState(null);         // { organization_provider, location_override, effective }
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingTab, setUploadingTab] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'sales');
  const [providerOverride, setProviderOverride] = useState(null);  // in-page provider preview
  const [savingProvider, setSavingProvider] = useState(false);

  const fileInputs = useRef({});

  // ---- Load catalog + effective provider + recent imports
  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [cat, eff, rec] = await Promise.all([
        api.get('/imports/providers'),
        api.get('/imports/effective-provider', {
          params: currentLocation ? { location_id: currentLocation.id } : {},
        }),
        api.get('/imports/recent', {
          params: currentLocation ? { location_id: currentLocation.id, limit: 20 } : { limit: 20 },
        }),
      ]);
      setCatalog(cat.data);
      setEffective(eff.data);
      setRecent(rec.data.items || []);
      setProviderOverride(eff.data?.effective || 'toast');
    } catch (err) {
      console.error(err);
      toast.error('Failed to load import options');
    } finally {
      setLoading(false);
    }
  }, [api, currentLocation]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Sync activeTab from URL param (deep-linking from other pages)
  useEffect(() => {
    const t = searchParams.get('tab');
    if (t && TAB_ORDER.some(x => x.id === t)) setActiveTab(t);
  }, [searchParams]);

  const provider = useMemo(() => {
    if (!catalog || !providerOverride) return null;
    return catalog.providers?.[providerOverride] || catalog.providers?.toast;
  }, [catalog, providerOverride]);

  // Tab config resolver: for pos tabs uses catalog.providers[provider].tabs[tabId]; for common uses common_tabs
  const getTabConfig = (tabId) => {
    if (!catalog) return null;
    const meta = TAB_ORDER.find(t => t.id === tabId);
    if (!meta) return null;
    if (meta.kind === 'pos') {
      return {
        ...meta,
        ...(provider?.tabs?.[tabId] || {}),
        provider_tab: true,
      };
    }
    return { ...meta, ...(catalog.common_tabs?.[tabId] || {}) };
  };

  // ---- Upload handler
  const handleUpload = async (tabId, files) => {
    const cfg = getTabConfig(tabId);
    if (!cfg?.endpoint) {
      toast.info(cfg?.note || 'Coming soon');
      return;
    }
    if (!currentLocation) {
      toast.error('Select a location first');
      return;
    }
    if (!files || files.length === 0) return;

    setUploadingTab(tabId);
    setLastResult(null);

    try {
      // Handle multi-file (receipts) sequentially so the user sees per-file results
      const filesList = Array.from(files);
      const results = [];
      for (const file of filesList) {
        const formData = new FormData();
        formData.append(cfg.file_field || 'file', file);
        formData.append('location_id', currentLocation.id);
        // Use `csv_endpoint` when the user picked a single CSV instead of a ZIP.
        const isCsv = /\.csv$/i.test(file.name || '');
        let endpoint = (isCsv && cfg.csv_endpoint) ? cfg.csv_endpoint : cfg.endpoint;
        // Defensive: axios `api` already has baseURL ending in /api, so strip a
        // leading /api if the catalog accidentally includes it. Prevents /api/api/...
        if (endpoint && endpoint.startsWith('/api/')) endpoint = endpoint.slice(4);
        const res = await api.post(endpoint, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        results.push({ name: file.name, ok: true, data: res.data });
      }
      setLastResult({ tabId, tabLabel: cfg.label, results });
      toast.success(`${cfg.label}: imported ${results.length} file${results.length > 1 ? 's' : ''}`);
      loadAll();
    } catch (err) {
      console.error('Import error:', err);
      const detail = err?.response?.data?.detail || 'Import failed';
      setLastResult({ tabId, tabLabel: cfg?.label, error: detail });
      toast.error(detail);
    } finally {
      setUploadingTab(null);
      // reset file input so same file can be re-selected
      if (fileInputs.current[tabId]) fileInputs.current[tabId].value = '';
    }
  };

  // ---- Provider setting handlers (org default vs location override)
  const setProvider = async (level, providerId) => {
    setSavingProvider(true);
    try {
      if (level === 'org') {
        const orgs = await api.get('/organizations');
        const orgId = orgs.data?.[0]?.id;
        await api.put(`/organizations/${orgId}/pos-provider`, { pos_provider: providerId });
        toast.success(`Default POS set to ${catalog.providers[providerId]?.name}`);
      } else if (level === 'location' && currentLocation) {
        await api.put(`/locations/${currentLocation.id}/pos-provider`, { pos_provider: providerId });
        toast.success(`${currentLocation.name}: POS override set to ${catalog.providers[providerId]?.name}`);
      } else if (level === 'clear' && currentLocation) {
        await api.put(`/locations/${currentLocation.id}/pos-provider`, { pos_provider: null });
        toast.success(`${currentLocation.name}: using org default`);
      }
      await loadAll();
    } catch (err) {
      console.error(err);
      toast.error('Could not update POS provider');
    } finally {
      setSavingProvider(false);
    }
  };

  if (loading || !catalog) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const handleTabChange = (val) => {
    setActiveTab(val);
    const next = new URLSearchParams(searchParams);
    next.set('tab', val);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="p-6 space-y-6" data-testid="imports-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Upload className="w-7 h-7 text-primary" />
            Imports
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Bring sales, orders, product mix, payroll, bank statements and receipts into Tillytics — all in one place.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAll} data-testid="refresh-imports-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Provider row */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-muted-foreground">Point of Sale for {currentLocation?.name || 'this location'}:</span>
              {provider && <ProviderChip provider={provider} />}
              {effective?.location_override ? (
                <Badge variant="secondary" className="text-[10px]">Location override</Badge>
              ) : (
                <Badge variant="outline" className="text-[10px]">Org default</Badge>
              )}
              {provider?.docs_url && (
                <a
                  className="text-xs text-primary flex items-center gap-1 hover:underline"
                  href={provider.docs_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="provider-docs-link"
                >
                  Docs <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Select
                value={providerOverride || 'toast'}
                onValueChange={(v) => setProviderOverride(v)}
                disabled={savingProvider}
              >
                <SelectTrigger className="w-[180px]" data-testid="provider-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(catalog.providers).map(p => (
                    <SelectItem key={p.id} value={p.id} data-testid={`provider-option-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setProvider('location', providerOverride)}
                disabled={savingProvider || !currentLocation}
                data-testid="set-location-provider-btn"
              >
                Save for {currentLocation?.name ? currentLocation.name.split(' ')[0] : 'location'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setProvider('org', providerOverride)}
                disabled={savingProvider}
                data-testid="set-org-provider-btn"
              >
                Set org default
              </Button>
              {effective?.location_override && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setProvider('clear', null)}
                  disabled={savingProvider}
                  className="text-muted-foreground"
                  data-testid="clear-location-override-btn"
                >
                  Use org default
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-3 md:grid-cols-6 h-auto">
          {TAB_ORDER.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.id} value={t.id} data-testid={`tab-${t.id}`} className="py-2">
                <Icon className="w-4 h-4 mr-1.5" />
                <span className="hidden sm:inline">{t.label}</span>
                <span className="sm:hidden text-xs">{t.label.split(' ')[0]}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {TAB_ORDER.map((t) => {
          const cfg = getTabConfig(t.id);
          const isUploading = uploadingTab === t.id;
          const comingSoon = cfg?.coming_soon || cfg?.manual_only || !cfg?.endpoint;
          const tabRecent = recent.filter(r => r.tab === t.id);
          return (
            <TabsContent key={t.id} value={t.id} className="mt-4 space-y-4">
              <Card>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <t.icon className="w-5 h-5" />
                      {cfg?.label}
                    </CardTitle>
                    {t.kind === 'pos' && provider && (
                      <ProviderChip provider={provider} />
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {cfg?.note && (
                    <div className="text-sm text-muted-foreground flex items-start gap-2">
                      <Info className="w-4 h-4 mt-0.5 shrink-0" />
                      <p>{cfg.note}</p>
                    </div>
                  )}

                  {cfg?.steps?.length > 0 && (
                    <ol className="text-sm text-muted-foreground space-y-1 pl-5 list-decimal">
                      {cfg.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  )}

                  <Separator />

                  {comingSoon ? (
                    <div className="py-8 text-center text-muted-foreground border border-dashed rounded-md" data-testid={`${t.id}-coming-soon`}>
                      <AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm font-medium">
                        {cfg?.manual_only
                          ? 'No import needed for this provider'
                          : `${provider?.name || 'This provider'} — ${cfg?.label} import is coming soon`}
                      </p>
                      {!cfg?.manual_only && (
                        <p className="text-xs mt-1">
                          Switch to a supported provider or contact Tillytics to prioritise this parser.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-3 p-6 border-2 border-dashed rounded-md">
                      <input
                        ref={(el) => (fileInputs.current[t.id] = el)}
                        type="file"
                        accept={cfg.accept}
                        multiple={!!cfg.multiple}
                        className="hidden"
                        id={`import-input-${t.id}`}
                        onChange={(e) => handleUpload(t.id, e.target.files)}
                        data-testid={`file-input-${t.id}`}
                      />
                      <Button
                        onClick={() => fileInputs.current[t.id]?.click()}
                        disabled={isUploading || !currentLocation}
                        size="lg"
                        data-testid={`import-btn-${t.id}`}
                      >
                        {isUploading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        {isUploading ? 'Importing…' : `Import ${cfg.label}`}
                      </Button>
                      <p className="text-xs text-muted-foreground">
                        Accepted: <code className="font-mono">{cfg.accept}</code>
                        {cfg.multiple && ' (multiple files supported)'}
                      </p>
                      {!currentLocation && (
                        <p className="text-xs text-amber-600">Select a location first</p>
                      )}
                    </div>
                  )}

                  {/* Last result banner (only for this tab) */}
                  {lastResult && lastResult.tabId === t.id && (
                    <div
                      className={`p-3 rounded-md text-sm ${lastResult.error ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'}`}
                      data-testid={`import-result-${t.id}`}
                    >
                      {lastResult.error ? (
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-4 h-4" />
                          {lastResult.error}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4" />
                          Imported: {lastResult.results.map(r => r.name).join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Recent for this tab */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">Recent imports</CardTitle>
                </CardHeader>
                <CardContent>
                  {tabRecent.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-2">Nothing here yet.</p>
                  ) : (
                    <ul className="divide-y" data-testid={`recent-list-${t.id}`}>
                      {tabRecent.slice(0, 8).map((item) => (
                        <li key={item.id} className="py-2 flex items-center justify-between text-sm">
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                            <span className="truncate">{item.filename || 'unnamed'}</span>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0 ml-2">
                            {formatDate(item.created_at)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          );
        })}
      </Tabs>
    </div>
  );
}
